// Аватар с инициалами и стопка аватаров (components/AvatarStack/README.md): круг с инициалами
// на color-accent-soft, текст color-accent, в стопке — белое кольцо 2 px. Стопка — до 3–4 человек,
// дальше «+N»; клик по стопке — действие потребителя (например, открыть доступ). У одиночного
// аватара — подсказка с ФИО; никого — пустой круг с пунктиром и подсказкой «Не назначен».
// В карточке пользователя меню — скруглённый квадрат 36 px.
import { cx } from '../lib/cx.ts';
import { Tooltip } from './Tooltip.tsx';

/** «Морозов Олег Сергеевич» → «МО»; «Морозов О. С.» → «МО». */
export function initials(fullName: string): string {
  const parts = fullName
    .split(/[\s.]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toLocaleUpperCase('ru-RU'))
    .join('');
}

export interface AvatarProps {
  fullName: string;
  /** Скруглённый квадрат 36 px — в карточке пользователя меню. */
  square?: boolean;
  className?: string;
}

/** Аватар рядом с ФИО: ФИО уже написано рядом, поэтому сам аватар скрыт от программ чтения. */
export function Avatar({ fullName, square = false, className }: AvatarProps) {
  return (
    <span className={cx('t-av', square && 'sq', className)} aria-hidden="true" title={fullName}>
      {initials(fullName)}
    </span>
  );
}

export interface AvatarPerson {
  id?: string;
  fullName: string;
}

export interface AvatarStackProps {
  people: readonly AvatarPerson[];
  /** Кто это: «Участники». Начинает подпись для программ чтения экрана. */
  label?: string;
  /**
   * Сколько аватаров до «+N». Если людей ровно на одного больше, видны все: «+1» занял бы
   * то же место, что последний аватар.
   */
  max?: number;
  /** Клик по стопке — например, открыть доступ. Без обработчика стопка — не кнопка. */
  onClick?: () => void;
  /** Подсказка пустого круга. */
  emptyLabel?: string;
  className?: string;
}

const DEFAULT_MAX = 3;

export function AvatarStack({
  people,
  label,
  max = DEFAULT_MAX,
  onClick,
  emptyLabel = 'Не назначен',
  className,
}: AvatarStackProps) {
  const names = people.map((person) => person.fullName).join(', ');
  const tip = people.length === 0 ? emptyLabel : names;
  const name = people.length > 0 && label ? `${label}: ${names}` : tip;
  const shown = people.length <= max + 1 ? people : people.slice(0, max);
  const rest = people.length - shown.length;

  const more =
    rest > 0 ? (
      <span className="t-av more" aria-hidden="true">
        +{rest}
      </span>
    ) : null;

  // Пустой круг сам по себе — картинка с подписью; стопка — общая картинка с ФИО всех людей.
  // В кнопке подпись — у кнопки.
  const look =
    people.length === 0 ? (
      <span
        className={cx('t-av kp-av-empty', !onClick && className)}
        role={onClick ? undefined : 'img'}
        aria-label={onClick ? undefined : name}
        aria-hidden={onClick ? true : undefined}
      />
    ) : (
      <span
        className={cx('t-avs', !onClick && className)}
        role={onClick ? undefined : 'img'}
        aria-label={onClick ? undefined : name}
      >
        {shown.map((person, index) => (
          <span
            key={person.id ?? `${index}-${person.fullName}`}
            className="t-av"
            aria-hidden="true"
          >
            {initials(person.fullName)}
          </span>
        ))}
        {more}
      </span>
    );

  return (
    <Tooltip content={tip}>
      {onClick ? (
        <button
          type="button"
          className={cx('kp-avs-btn', className)}
          aria-label={name}
          onClick={onClick}
        >
          {look}
        </button>
      ) : (
        look
      )}
    </Tooltip>
  );
}
